import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { lookupsApi, shippingApi, usersApi, type ShippingKind, type ShippingRequest } from '@/lib/apiClient';
import { Button, StateView } from '@/components';
import ShippingDrawer from './ShippingDrawer';
import ShippingList from './ShippingList';
import { ShippingStyles } from './ShippingStyles';
import { CHANGE_NOTICE, KINDS, KIND_PLURALS, type ShippingChange } from './shippingForm';

/**
 * The Shipping page (#717, #718, #719): Logistics' working list of pickup and delivery requests across every job,
 * and a way for a PM or fabrication to raise one.
 *
 * The ORDER is the API's, not this page's: open requests first, Expedited at the top, then by the date they are
 * needed. Reading it needs `receiving.edit` or view-all (the list carries onsite contacts' phone numbers); the route
 * and the navigation tab are gated the same way, and the API is what actually refuses anyone else.
 */
export default function ShippingPage() {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [kind, setKind] = useState<ShippingKind | ''>('');
  const [statusId, setStatusId] = useState('');
  const [includeClosed, setIncludeClosed] = useState(false);
  const [drawer, setDrawer] = useState<{ item: ShippingRequest | null } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // One request per pause in typing, not one per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  const canEdit = (me?.roles.includes('Admin') ?? false) || (me?.permissions.includes('receiving.edit') ?? false);

  const params = useMemo(() => ({
    search: debounced || undefined,
    kind: kind || undefined,
    statusId: statusId ? Number(statusId) : undefined,
  }), [debounced, kind, statusId]);
  const query = useQuery({ queryKey: ['shipping', 'all', params], queryFn: () => shippingApi.list(params) });

  const { data: statuses } = useQuery({ queryKey: ['shipping-statuses'], queryFn: () => lookupsApi.listShippingStatuses(), staleTime: 30 * 60 * 1000 });

  const onSaved = (change: ShippingChange) => { setDrawer(null); setNotice(CHANGE_NOTICE[change]); };

  return (
    <div>
      <ShippingStyles />
      <div className="shp-top">
        <h2 className="shp-title">Shipping</h2>
        {canEdit && <Button size="sm" onClick={() => { setNotice(null); setDrawer({ item: null }); }}>＋ New request</Button>}
      </div>
      <p className="shp-muted" style={{ margin: '0 0 12px' }}>
        Requests to pick something up or deliver it. Open ones come first, and Expedited ones sit at the top.
      </p>

      <div className="shp-filters">
        <input type="search" aria-label="Search requests" placeholder="Search items, places, contacts, jobs…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select aria-label="Type" value={kind} onChange={(e) => setKind(e.target.value as ShippingKind | '')}>
          <option value="">Pickups and deliveries</option>
          {KINDS.map((k) => <option key={k} value={k}>{KIND_PLURALS[k]}</option>)}
        </select>
        <select aria-label="Status" value={statusId} onChange={(e) => setStatusId(e.target.value)}>
          <option value="">All open</option>
          {(statuses ?? []).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      {notice && <p className="shp-ok" role="status">{notice}</p>}

      <div className="shp-card">
        <StateView query={query} empty={<p className="shp-pad shp-muted">No requests match.</p>}>
          {(rows) => {
            // Naming a status shows it as asked. Otherwise closed requests (Completed, Cancelled) stay out of the way.
            const open = statusId ? rows : rows.filter((r) => r.status?.phase !== 'Closed');
            const shown = includeClosed || statusId ? rows : open;
            const hidden = rows.length - open.length;
            return (
              <>
                {!statusId && hidden > 0 && (
                  <label className="shp-check shp-pad">
                    <input type="checkbox" checked={includeClosed} onChange={(e) => setIncludeClosed(e.target.checked)} />
                    Include completed and cancelled <span className="shp-hidden-count">{hidden}</span>
                  </label>
                )}
                {shown.length === 0
                  ? <p className="shp-pad shp-muted">No open requests match.</p>
                  : <ShippingList requests={shown} showJob onOpen={(r) => { setNotice(null); setDrawer({ item: r }); }} />}
              </>
            );
          }}
        </StateView>
      </div>

      {drawer && (
        <ShippingDrawer item={drawer.item} readOnly={!canEdit} onClose={() => setDrawer(null)} onSaved={onSaved} />
      )}
    </div>
  );
}
